import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { OpportunityContactType } from "@/lib/validations/schema";
import { ColumnDef } from "@tanstack/react-table";
import { AxiosRequestConfig } from "axios";
import { channel_options, close_reason_options } from "common/filters";
import { Img, Spinner } from "components";
import { DataTable } from "components/Table/DataTable";
import DocumentModal from "models/DocumentPopup";
import LinkCorporate from "models/LinkCorporate";
import LinkPartner from "models/LinkPartner";
import { CreatePartnerLeadType } from "pages/CreatePartnerLead/schema";
import { ChannelOptions } from "pages/Leads/lead";
import { useEffect, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { resetState } from "redux/features/CreateOpportunity";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  downloadDoc,
  getCompanyById,
  GetCompanyByIdReqType,
  getContactDetailById,
  getOpportunityByCompany,
  GetOpportunityByCompanyReqType,
  GetOpportunityByIdReqType,
  PutUpdateCompanyDetails,
  PutUpdateCompanyDetailsReqType,
} from "service/apis/Opportunities";
import { toast, Toaster } from "sonner";
import EditForm from "./editform";

import { buttonInfo, getRelativeDate } from "./utils/featureRule";
import { checkLevel } from "common/rules";
import { LEVEL_2 } from "common/constants";
import { LeadOverView } from "./LeadOverView";
import {
  setContactId,
  setEmail,
  setIsReferralMade,
  setPhone,
} from "redux/features/CustomerRegistration";
import { handleDownloadViaURL } from "utils";
import { RowDetailsSheet } from "./opportunitydetails";

const columns: ColumnDef<OpportunityContactType>[] = [
  {
    accessorKey: "id",
    cell: ({ row }) => {
      const item = row.original;
      return (
        <div className="w-full border rounded-lg p-4 mb-3 hover:bg-gray-50 cursor-pointer">
          <div className="flex justify-between items-start mb-2">
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-lg">{item.product}</h3>
                {item.requestQuote_Type && (
                  <div className="relative group flex items-center">
                    <div className="w-2.5 h-2.5 bg-red-500 rounded-full"></div>
                    <span className="absolute bottom-full left-0 mb-1 bg-gray-300 text-[#333] text-xs rounded-md py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                      Quotation request for ID: {item.id}
                    </span>
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-500">{item.subCategory}</p>
            </div>
            <div>
              <p
                className={`py-1 px-3 rounded-full text-center ${
                  item.opportunityContactStatus
                    ? close_reason_options.find(
                        (opt) => opt.value === item.opportunityContactStatus,
                      )?.bgColor +
                      " " +
                      close_reason_options.find(
                        (opt) => opt.value === item.opportunityContactStatus,
                      )?.textColor
                    : "bg-[#E1FFE2] text-[#23AB28]"
                }`}
              >
                {item.opportunityContactStatus
                  ? close_reason_options.find(
                      (opt) => opt.value === item.opportunityContactStatus,
                    )?.label
                  : "Open"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1 text-gray-600 mb-2">
            <span className="font-medium">{item.id}</span>
            <span className="mx-2">•</span>
            <span>{item.createDate}</span>
            <span className="ml-1 text-xs text-gray-500">
              ({item.createdBy})
            </span>
          </div>

          <div className="flex items-center gap-2 mb-2">
            <div className="flex items-center">
              <svg
                viewBox="0 0 24 24"
                className="w-5 h-5 text-gray-500"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z"
                  fill="currentColor"
                />
              </svg>
              <span className="ml-1">{item.contactPerson || item.name}</span>
            </div>
            <span className="text-gray-400 mx-1">•</span>
            <div className="text-sm text-gray-500">{item.location}</div>
          </div>

          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <span className="text-gray-600">Assigned to:</span>
              <span className="ml-1 font-medium">{item.assignedTo}</span>
            </div>

            <div className="flex items-center">
              {(() => {
                const newSource = (channel_options as ChannelOptions)?.[
                  (item?.lead_deatils?.Type as string)?.toLowerCase()
                ]?.find(
                  (opt) =>
                    opt.value?.toLowerCase() ===
                    (item.source as string)?.toLowerCase().split(" ").join(""),
                );

                return (
                  <div className="flex items-center gap-2">
                    <span className="text-sm">
                      {item?.lead_deatils?.id === 12
                        ? "Partner referral"
                        : item?.lead_deatils?.id === 18
                          ? "Employee referral"
                          : newSource?.label || item.source}
                    </span>
                    {newSource?.icon && (
                      <Img
                        className="h-5 w-5"
                        src={newSource.icon}
                        alt="icon"
                      />
                    )}
                  </div>
                );
              })()}

              {checkLevel(LEVEL_2) && item.latestQuotationPDF && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDownloadViaURL(item.latestQuotationPDF as string);
                  }}
                  className="ml-3"
                >
                  <svg
                    width="25"
                    height="24"
                    viewBox="0 0 25 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3.59375 5.43359C3.59375 2.67217 5.83232 0.433594 8.59375 0.433594H15.537C15.8914 0.433594 16.2291 0.583994 16.4661 0.847394L21.3979 6.32706C21.6045 6.55659 21.7188 6.85446 21.7188 7.16327V18.5586C21.7188 21.32 19.4801 23.5586 16.7188 23.5586H8.59375C5.83232 23.5586 3.59375 21.32 3.59375 18.5586V5.43359Z"
                      fill="#165FE3"
                    />
                    <path
                      d="M21.7188 7.93359V7.16327C21.7188 6.85446 21.6045 6.55659 21.3979 6.32706L16.4661 0.847394C16.2291 0.583994 15.8914 0.433594 15.537 0.433594H14.2188V2.93359C14.2188 5.69502 16.4574 7.93359 19.2188 7.93359H21.7188Z"
                      fill="#98BDFF"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M8.26385 14.4462C8.64095 14.0913 9.23427 14.1094 9.58906 14.4865L12.6562 17.7467L15.7235 14.4865C16.0782 14.1094 16.6715 14.0913 17.0486 14.4462C17.4257 14.8009 17.4439 15.3942 17.089 15.7713L13.7179 19.3545C13.1385 19.9704 12.174 19.9704 11.5946 19.3545L8.22345 15.7713C7.86866 15.3942 7.88675 14.8009 8.26385 14.4462Z"
                      fill="white"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M12.6562 8.56641C13.174 8.56641 13.5938 8.98614 13.5938 9.50391V18.8789C13.5938 19.3967 13.174 19.8164 12.6562 19.8164C12.1385 19.8164 11.7188 19.3967 11.7188 18.8789V9.50391C11.7188 8.98614 12.1385 8.56641 12.6562 8.56641Z"
                      fill="white"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      );
    },
    enableHiding: false,
  },
];

const filteredColumns = checkLevel(LEVEL_2)
  ? columns.filter((col) => col.id !== "source")
  : columns;

const tablist = [
  { label: "Leads", value: "leads", enabled: true },
  { label: "Orders", value: "orders", enabled: false },
];
export default function Mobilecontactdetail() {
  const formContext = useFormContext<CreatePartnerLeadType>();
  const { id } = useParams();
  const location = useLocation();

  useEffect(() => {
    if (id && formContext) {
      formContext.setValue("contact_id", id, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
      console.log("formcontext", formContext);
      console.log("Setting contact_id to:", id);
    }
  }, [id, formContext]);

  const [isOpen, setIsOpen] = useState(false);
  const [opportunityList, setOpportunityList] = useState<
    OpportunityContactType[]
  >([]);
  const [isRowDetailsOpen, setIsRowDetailsOpen] = useState(false);

  const [selectedRowData, setSelectedRowData] =
    useState<OpportunityContactType | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [companyData, setCompanyDetails] = useState<any>([]);

  const [docs, setDocs] = useState<
    {
      doc_id: string;
      filename: string;
      file: string;
      date: string;
    }[]
  >([]);
  const [sheetSide, setSheetSide] = useState("right");
  const [activeTab, setActiveTab] = useState("leads");

  const navigate = useNavigate();

  const methods = useForm({
    defaultValues: companyData,
  });
  const { pipelineType } = useAppSelector((state) => state.CreateOpportunity);
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(companyData?.notes || "");

  useEffect(() => {
    methods.reset(companyData);
  }, [companyData]);

  const handleEditClick = () => {
    setInputValue(companyData?.notes || "");
    setIsEditing(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
  };

  const handleSave = async () => {
    const auth = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);

    const payload: AxiosRequestConfig<PutUpdateCompanyDetailsReqType> = {
      headers: { Authorization: `Bearer ${authToken}` },
      data: {
        id: id as string,
        notes: inputValue,
        pipelineType: pipelineType,
      },
    };

    try {
      const res = await PutUpdateCompanyDetails(payload);
      if (res.status === 200) {
        toast.success("Notes updated successfully");
        setCompanyDetails((prev) => ({ ...prev, notes: inputValue }));
        setIsEditing(false);
      } else {
        console.error("Update failed:", res);
        toast.error("Failed to update notes: " + res.statusText);
      }
    } catch (error) {
      console.error("Error updating notes:", error);
      toast.error(
        "Failed to update notes: " +
          (error.response?.data?.message || error.message),
      );
    }
  };

  const handleRowClick = (row: OpportunityContactType) => {
    setSelectedRowData(row);
    setIsRowDetailsOpen(true);
  };

  const handleDialogOpen = (open: boolean) => {
    setDialogOpen(open);
  };

  const dispatch = useAppDispatch();
  const handleSheetOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      dispatch(resetState());
    }
  };

  const handleUpdateCompany = async (value: any) => {
    setLoading(true);
    const auth = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);

    let payload: AxiosRequestConfig<PutUpdateCompanyDetailsReqType> = {
      headers: { Authorization: `Bearer ${authToken}` },
    };

    if (pipelineType === "corporate") {
      payload.data = {
        id: id as string,
        company_name: value?.company_name,
        email: value?.company_email,
        contactPersonType: value?.contact_person_type?.value,
        contactName: value?.contact_person_name,
        contactNumber: value?.contact_person_phone,
        pipelineType: "coorporate",
      };
    }
    if (pipelineType === "partner") {
      payload.data = {
        id: id as string,
        contactName: value?.entity_name,
        contactEmail: value?.company_email,
        typeOfPartner: value?.entity_type?.value,
        pipelineType: "partner",
      };
    }
    if (pipelineType === "retail") {
      payload.data = {
        id: id as string,
        contactName: value?.contact_name,
        contactNumber: value?.contact_number,
        contactEmail: value?.contact_email,
        address: value?.contact_address,
        pipelineType: "retail",
        notes: inputValue,
        contactAddress: [
          ...companyData?.contactAddress,
          value?.contact_address,
        ],
      };
    }

    try {
      const res = (await PutUpdateCompanyDetails(payload)) as any;
      if (res.status === 200) {
        toast.success("Contact details updated successfully");
        handleDialogOpen(false);
        await getCompanyDetails();
        setLoading(false);
      }
    } catch (error) {
      toast.error("Failed to update contact details");
      setLoading(false);
    }
  };

  const getCompanyDetails = async () => {
    const auth = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);
    const payload: AxiosRequestConfig<GetCompanyByIdReqType> = {
      headers: { Authorization: `Bearer ${authToken}` },
      data: {
        id,
      },
    };
    try {
      let response;
      if (!id) return;
      if (pipelineType === "corporate") {
        response = (await getCompanyById(payload)) as any;
      } else {
        response = (await getContactDetailById(payload)) as any;
      }
      setCompanyDetails(response?.data);
    } catch (error) {
      console.error("Error fetching options:", error);
    }
  };

  const getOpportunityList = async () => {
    const auth = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);
    let payload: AxiosRequestConfig<GetOpportunityByCompanyReqType> = {
      headers: { Authorization: `Bearer ${authToken}` },
      params: {
        contactID: id,
      },
    };
    if (pipelineType === "corporate") {
      payload = {
        headers: { Authorization: `Bearer ${authToken}` },
        params: {
          companyID: id,
        },
      };
    }

    const name = localStorage.getItem("owner_name")?.toString();
    try {
      const response = (await getOpportunityByCompany(payload)) as any;
      if (response?.data?.length === 0) {
        return;
      }
      setOpportunityList(() => {
        return response?.data?.map((item: any) => {
          // const item= items?.leadDetail
          return {
            id: item?.id,
            uniqueId: item?.unique_id,
            contactPerson: item?._contacts?.[0]?.contactName,
            product: item?._product?.name,
            assignedTo: item?.assignedTo !== 0 ? item?._staff?.fname : name,
            createDate: getRelativeDate(item?.created_at),
            source: item?._channel_of_interaction?.[0]?.Channel,
            opportunityContactStatus: item?.reasonToClose,
            contactNumber: item?._contacts?.[0]?.contactNumber,
            subCategory: item?._product?.subCategory,
            location: item?._staff?.city,
            createdBy: item?.createdBy !== 0 ? item?._createdBy?.fname : name,
            lead_deatils: item?._channel_of_interaction?.[0],
            referred_partner: item?._refferedBy_partner?.name,
            referred_employee: item?._refferedBy_employee?.name,
            requestQuote_Type: item?.quotation_req,
            req_book_rate: item?.req_book_rate,
            purpose_id: item?.purpose_id,
            channel_of_interaction_id: item?._channel_of_interaction?.[0]?.id,
            contacts_id: item?._contacts?.[0]?.id,
            latestQuotationPDF:
              item?.interactions_id?.at(-1)?.[0]?.quotation_pdf,
            name: item?._contacts?.[0]?.contactName,
            mode_opp: item?.mode_opp,
            took_loan: item?.took_loan === true ? "With Loan" : "Without Loan",
            amount: item?.amount,
            country: item?.country,
            currency: item?.currency,
          };
        });
      });
    } catch (error) {
      console.error("Error fetching options:", error);
    }
  };

  const getDocsForDownload = async () => {
    const auth = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);
    const payload: AxiosRequestConfig<GetOpportunityByIdReqType> = {
      headers: { Authorization: `Bearer ${authToken}` },
      params: {
        contacts_id: id,
      },
    };
    try {
      const response = await downloadDoc(payload);
      if (response?.data?.length === 0) {
        return;
      }
      setDocs(
        () =>
          response?.data
            ?.map(
              (file: {
                doc_id: string;
                filename: string;
                file: { url: string };
                req_time: string;
              }) => {
                if (file?.file?.url) {
                  return {
                    doc_id: file.doc_id,
                    filename: file.filename,
                    file: file.file.url,
                    date: file?.req_time,
                  };
                }
                return null;
              },
            )
            .filter((file) => file !== null) as {
            doc_id: string;
            filename: string;
            file: string;
            date: string;
          }[],
      );
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error("Document view limit exceeded.", {
          description:
            "You have exceeded the limit of requests. Please try again later.",
        });
      }
      console.error("Error fetching docs:", error);
    }
  };

  const handleDownload = (url: string, filename: string) => {
    fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch((error) => toast.error("Documnet file is not available."));
  };

  const handleDownloadAll = () => {
    docs.forEach((doc: any, index) => {
      // Add a slight delay to each download to prevent overwhelming the browser
      setTimeout(() => {
        handleDownload(doc?.file?.url, doc?.filename);
      }, index * 500); // 500ms delay between each download
    });
  };

  useEffect(() => {
    getCompanyDetails();
  }, [isOpen, id]);

  useEffect(() => {
    getOpportunityList();
  }, [isOpen, id]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setSheetSide("bottom");
      } else {
        setSheetSide("right");
      }
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { label, href } = buttonInfo();

  // set hash route on load with compantData.name
  useEffect(() => {
    if (companyData?.contactName) {
      const hash = `#${
        companyData?.company_name
          ? companyData?.company_name
          : companyData?.contactName
      }`;
      window.location.hash = hash;
    }
  }, [companyData?.contactName]);

  return (
    <>
      {loading && <Spinner />}
      <div className="flex gap-2.5 h-full sm:px-0 w-full flex-col font-inter font-thin px-4">
        {/* <div className=" flex w-full justify-between items-center py-4 pl-5 border-b">
            <div className=" flex flex-col gap-2">
              <h3 className=" font-semibold leading-4 text-base text-black-label">
                <span className=" capitalize">
                  {companyData?.company_name
                    ? companyData?.company_name
                    : companyData?.contactName}
                </span>
              </h3>
            </div>
            {!checkLevel(LEVEL_2) && (
              <div className="flex flex-row gap-1">
                {pipelineType === "retail" && <div className=""></div>}
                {companyData?.linked_partner_id !== 0 && (
                  <a
                    onClick={() => {
                      if (companyData?.linked_partner?.status === "lead") {
                        navigate(
                          `/leads/partner/${companyData?.linked_partner_id}`,
                        );
                      } else {
                        navigate(
                          `/partnerDetails/${companyData?.linked_partner_id}`,
                        );
                      }
                    }}
                    className="text-[#165FE3] font-inter text-sm font-semibold leading-4 tracking-[0.02em] text-left cursor-pointer"
                  >
                    Partner
                  </a>
                )}
                {companyData?.linked_company_id !== 0 && (
                  <a
                    onClick={() => {                      
                      if (companyData?.linked_company?.[0].status === "lead") {
                        navigate(
                          `/leads/corporate/${companyData?.linked_company_id}`,
                        );
                      } else {
                        navigate(`#`);
                      }
                    }}
                    className="text-[#165FE3] font-inter text-sm font-semibold leading-4 tracking-[0.02em] text-left cursor-pointer"
                  >
                    Corporate
                  </a>
                )}
              </div>
            )}
          </div> */}
        <div className="flex bg-white-A700 p-2.5 gap-5 w-full rounded-lg justify-between sm:flex-col">
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem
              value="item-1"
              className="border-none rounded-[8px] gap-5 no-underline bg-[#FAFAFA]"
            >
              <AccordionTrigger className="  border-none p-2.5 no-underline hover:no-underline">
                <div className="flex items-center gap-2">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5.33594 8.13281H10.0026"
                      stroke="#666666"
                      stroke-miterlimit="10"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M5.33594 10.8008H8.25594"
                      stroke="#666666"
                      stroke-miterlimit="10"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M6.66927 3.9987H9.33594C10.6693 3.9987 10.6693 3.33203 10.6693 2.66536C10.6693 1.33203 10.0026 1.33203 9.33594 1.33203H6.66927C6.0026 1.33203 5.33594 1.33203 5.33594 2.66536C5.33594 3.9987 6.0026 3.9987 6.66927 3.9987Z"
                      stroke="#666666"
                      stroke-miterlimit="10"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M10.6667 2.67969C12.8867 2.79969 14 3.61969 14 6.66635V10.6664C14 13.333 13.3333 14.6664 10 14.6664H6C2.66667 14.6664 2 13.333 2 10.6664V6.66635C2 3.62635 3.11333 2.79969 5.33333 2.67969"
                      stroke="#666666"
                      stroke-miterlimit="10"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>

                  <p className="">Contact Details & Internal Notes</p>
                </div>
              </AccordionTrigger>
              <AccordionContent className="flex pl-2 flex-col mt-2 py-1 rounded-xl border-none">
                <div className="bg-[#fafafa] p-2 rounded-xl w-full relative">
                  <div className="grid relative md:grid-cols-1 sm:grid-cols-1 w-full gap-5">
                    {!checkLevel(LEVEL_2) && (
                      <div className="flex float-right absolute top-1 -right-1 gap-2">
                        <AlertDialog open={dialogOpen}>
                          <AlertDialogTrigger
                            onClick={() => handleDialogOpen(!dialogOpen)}
                          >
                            <div className=" cursor-pointer ">
                              <svg
                                width="20"
                                height="21"
                                viewBox="0 0 20 21"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <g clipPath="url(#clip0_1689_36824)">
                                  <path
                                    d="M3.83594 4.82812H3.0026C2.56058 4.82813 2.13665 5.00372 1.82409 5.31628C1.51153 5.62884 1.33594 6.05276 1.33594 6.49479V13.9948C1.33594 14.4368 1.51153 14.8607 1.82409 15.1733C2.13665 15.4859 2.56058 15.6615 3.0026 15.6615H10.5026C10.9446 15.6615 11.3686 15.4859 11.6811 15.1733C11.9937 14.8607 12.1693 14.4368 12.1693 13.9948V13.1615"
                                    stroke="#165FE3"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M11.3333 3.16676L13.8333 5.66676M14.9875 4.48759C15.3157 4.15938 15.5001 3.71424 15.5001 3.25009C15.5001 2.78594 15.3157 2.34079 14.9875 2.01259C14.6593 1.68438 14.2142 1.5 13.75 1.5C13.2858 1.5 12.8407 1.68438 12.5125 2.01259L5.5 9.00009V11.5001H8L14.9875 4.48759Z"
                                    stroke="#165FE3"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </g>
                                <defs>
                                  <clipPath id="clip0_1689_36824">
                                    <rect
                                      width="20"
                                      height="20"
                                      fill="white"
                                      transform="translate(0 0.5)"
                                    />
                                  </clipPath>
                                </defs>
                              </svg>
                            </div>
                          </AlertDialogTrigger>
                          <AlertDialogPortal>
                            <AlertDialogOverlay className=" bg-black-900 bg-opacity-60 animate-overlayShow fixed inset-0" />
                            <AlertDialogContent className="bg-white-A700 rounded-lg p-0 max-w-2xl w-full animate-contentShow sm:max-w-[90%]">
                              <AlertDialogHeader className="flex text-left border-b  rounded-t py-5 px-10 ">
                                <AlertDialogTitle className="font-bold capitalize p-0 text-lg text-black-label">
                                  Edit {pipelineType} contact details
                                </AlertDialogTitle>
                                <AlertDialogDescription className="font-medium p-0 text-sm  text-[#7E7E7E] ">
                                  Fill the required details
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <FormProvider {...methods}>
                                <div className="h-[500px] overflow-auto w-full">
                                  <EditForm />
                                </div>
                              </FormProvider>
                              <AlertDialogFooter className="flex justify-start items-center gap-2.5 py-5 px-10 flex-row rounded-b-lg bg-gray-button-gray-lite">
                                <AlertDialogCancel
                                  className=" m-0 rounded-lg border-blue-700 border py-[11px] px-4 text-blue-700"
                                  asChild
                                >
                                  <button
                                    onClick={() => {
                                      methods.reset();
                                      methods.clearErrors();
                                      setDialogOpen(false);
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </AlertDialogCancel>
                                <AlertDialogAction
                                  className="rounded-lg border-blue-700 py-[11px] px-4 bg-blue-700 border text-white-A700"
                                  asChild
                                >
                                  <button
                                    className=""
                                    disabled={!methods.formState.isDirty}
                                    onClick={methods.handleSubmit(
                                      handleUpdateCompany,
                                    )}
                                  >
                                    Submit
                                  </button>
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialogPortal>
                        </AlertDialog>
                      </div>
                    )}
                    {pipelineType === "retail" && (
                      <>
                        <div className="flex items-center gap-2.5">
                          <div className="">
                            <svg
                              width="20"
                              height="21"
                              viewBox="0 0 20 21"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M4.03832 13.8267L4.03824 13.8266C2.4108 11.8712 1.31349 10.0388 0.585923 8.06396C0.111832 6.78722 -0.0725525 5.72711 0.00693179 4.72728L0.0069486 4.72707C0.0742514 3.92365 0.398545 3.22884 0.970445 2.65694L2.52038 1.107L2.52123 1.10615L2.52124 1.10616C2.97237 0.676114 3.48987 0.45 4.03108 0.45C4.57615 0.45 5.08956 0.675888 5.52435 1.10202C5.80703 1.36394 6.09357 1.65466 6.367 1.93637L6.37833 1.94804C6.51528 2.08901 6.64833 2.22598 6.78919 2.36684L8.024 3.60166C8.02404 3.60169 8.02407 3.60172 8.02411 3.60176C8.48331 4.05679 8.72675 4.58699 8.72675 5.12909C8.72675 5.67141 8.47898 6.20155 8.02417 6.65637C7.89564 6.78489 7.76746 6.91307 7.64347 7.04119L7.64331 7.04137C7.29903 7.39375 6.94201 7.75494 6.56121 8.101C6.83589 8.72675 7.22748 9.34413 7.80816 10.0791C8.97807 11.5136 10.2008 12.6286 11.5465 13.4831L11.5465 13.4831C11.6809 13.5686 11.8321 13.6463 11.9979 13.7292L11.9984 13.7295C12.1201 13.7903 12.25 13.8552 12.3774 13.9271L13.8341 12.4704C14.2769 12.0277 14.7988 11.7886 15.3448 11.7886C15.8907 11.7886 16.4087 12.0276 16.8393 12.4749C16.8394 12.475 16.8395 12.4752 16.8396 12.4753L19.3296 14.9653C19.783 15.4187 20.0125 15.9458 20.0135 16.4795C20.0146 17.013 19.7874 17.5472 19.3386 18.0149C19.1473 18.2146 18.9478 18.4099 18.7528 18.5966C18.7528 18.5966 18.7528 18.5966 18.7528 18.5966L4.03832 13.8267ZM4.03832 13.8267C6.01544 16.1901 8.39569 18.0512 11.1125 19.368L11.1129 19.3682M4.03832 13.8267L11.1129 19.3682M11.1129 19.3682C12.1459 19.8578 13.5331 20.4415 15.0805 20.5416L15.0815 20.5417M11.1129 19.3682L15.0815 20.5417M15.0815 20.5417L15.0934 20.5422M15.0815 20.5417L15.0934 20.5422M4.74134 1.91097L4.74138 1.91093C4.51188 1.6774 4.26707 1.56533 4.03108 1.56533C3.79038 1.56533 3.53711 1.67791 3.30359 1.90326L1.76225 3.4446C1.37284 3.834 1.16652 4.28297 1.11779 4.82251C1.0523 5.64574 1.21172 6.55392 1.63696 7.68926L1.63702 7.68942C2.3226 9.53968 3.35954 11.2671 4.90243 13.1244C6.77527 15.3652 9.03235 17.1303 11.6035 18.3745C12.5515 18.8257 13.8082 19.3525 15.1574 19.4389C15.2279 19.4431 15.301 19.4471 15.3697 19.4471C16.0896 19.4471 16.6436 19.2123 17.11 18.7098C17.3701 18.3986 17.656 18.121 17.9308 17.8543C17.9468 17.8388 17.9629 17.8232 17.9788 17.8077L17.9791 17.8074C18.1737 17.6211 18.3552 17.4437 18.5283 17.2583L18.5288 17.2577C18.7761 17.0003 18.8952 16.7457 18.8972 16.4999C18.8991 16.2546 18.7843 16.0077 18.5419 15.7654L16.043 13.2706L16.0417 13.2692L16.0417 13.2692C15.8836 13.099 15.6391 12.9163 15.3448 12.9163C15.1052 12.9163 14.8561 13.0362 14.6218 13.2705L13.0718 14.8205C13.0004 14.8919 12.7722 15.1169 12.419 15.1169C12.2776 15.1169 12.1373 15.0815 11.9944 15.0079C11.9943 15.0079 11.9942 15.0078 11.9941 15.0078L12.017 14.9633C12.0087 14.9592 12.0015 14.955 11.9942 14.9509C11.9869 14.9467 11.9797 14.9426 11.9714 14.9385L4.74134 1.91097ZM4.74134 1.91097L4.74308 1.91258C4.98288 2.13448 5.21025 2.36567 5.45043 2.60989C5.48723 2.64732 5.52434 2.68505 5.56184 2.72309L4.74134 1.91097ZM15.0934 20.5422C15.1843 20.5461 15.2729 20.55 15.3655 20.55M15.0934 20.5422L15.3655 20.55M15.3655 20.55C16.4105 20.55 17.2526 20.1884 17.9378 19.4448M15.3655 20.55L17.9378 19.4448M17.9378 19.4448C17.9413 19.4413 17.9518 19.4309 17.9597 19.4178L17.9378 19.4448Z"
                                fill="#999999"
                                stroke="#999999"
                                strokeWidth="0.1"
                              />
                            </svg>
                          </div>

                          <p className="font-normal text-base leading-[25px] text-black-label">
                            {companyData?.contactNumber || (
                              <span className="text-gray-400">
                                Not available
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center gap-2.5">
                          <div className="">
                            <svg
                              width="20"
                              height="17"
                              viewBox="0 0 20 17"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M1.10812 3.83305C0.947499 3.72606 0.815788 3.58106 0.72469 3.41092C0.633593 3.24078 0.58593 3.05077 0.585938 2.85778V2.8568C0.585938 2.16395 1.14765 1.60227 1.84054 1.60227H18.1594C18.8522 1.60227 19.4139 2.16395 19.4139 2.8568V2.85778C19.4139 3.24969 19.2181 3.61559 18.8918 3.83305C17.2992 4.89496 12.5006 8.09382 10.6963 9.29694C10.4901 9.43444 10.2478 9.50781 9.99995 9.50781C9.75212 9.50781 9.50984 9.43444 9.30365 9.29694C7.49928 8.09382 2.70066 4.89496 1.10812 3.83305Z"
                                stroke="#999999"
                                strokeWidth="1.3"
                                strokeMiterlimit="10"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M0.585938 2.85426V14.1511C0.585894 14.3159 0.618355 14.4791 0.681462 14.6314C0.744568 14.7837 0.837082 14.922 0.95371 15.0385C1.07019 15.1551 1.20853 15.2476 1.3608 15.3107C1.51307 15.3738 1.6763 15.4063 1.84113 15.4062H18.1587C18.3236 15.4063 18.4868 15.3738 18.6391 15.3107C18.7913 15.2476 18.9297 15.1551 19.0462 15.0385C19.1628 14.922 19.2553 14.7837 19.3184 14.6314C19.3815 14.4791 19.414 14.3159 19.4139 14.1511V2.85426"
                                stroke="#999999"
                                strokeWidth="1.3"
                                strokeMiterlimit="10"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                          <p className="font-normal text-base leading-[25px] text-black-label">
                            {companyData?.contactEmail || (
                              <span className="text-gray-400">
                                Not available
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="flex w-full items-center gap-2.5">
                          <div className="">
                            <svg
                              width="16"
                              height="21"
                              viewBox="0 0 16 21"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M8 0.5C4.12297 0.5 0.96875 3.65422 0.96875 7.53125C0.96875 8.84117 1.3316 10.1195 2.01832 11.2286L7.59977 20.223C7.65233 20.3077 7.72564 20.3776 7.81276 20.4261C7.89988 20.4746 7.99792 20.5 8.09762 20.5H8.10227C8.20274 20.4992 8.30133 20.4726 8.38855 20.4227C8.47577 20.3728 8.54869 20.3014 8.60031 20.2152L14.0395 11.1336C14.6883 10.048 15.0312 8.80231 15.0312 7.53125C15.0312 3.65422 11.877 0.5 8 0.5ZM13.0338 10.532L8.08875 18.7885L3.01434 10.6112C2.44273 9.68805 2.13281 8.62305 2.13281 7.53125C2.13281 4.30039 4.76914 1.66406 8 1.66406C11.2309 1.66406 13.8633 4.30039 13.8633 7.53125C13.8633 8.59066 13.5738 9.62844 13.0338 10.532Z"
                                fill="#999999"
                              />
                              <path
                                d="M8 4.01562C6.06148 4.01562 4.48438 5.59273 4.48438 7.53125C4.48438 9.45738 6.03582 11.0469 8 11.0469C9.9884 11.0469 11.5156 9.43621 11.5156 7.53125C11.5156 5.59273 9.93852 4.01562 8 4.01562ZM8 9.88281C6.7009 9.88281 5.64844 8.82684 5.64844 7.53125C5.64844 6.23891 6.70766 5.17969 8 5.17969C9.29234 5.17969 10.3477 6.23891 10.3477 7.53125C10.3477 8.80793 9.31969 9.88281 8 9.88281Z"
                                fill="#999999"
                              />
                            </svg>
                          </div>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger
                                className="flex cursor-pointer items-end"
                                asChild
                              >
                                <p className=" w-full font-normal text-base leading-[25px] text-black-label">
                                  {companyData?.contactAddress
                                    ?.at(-1)
                                    ?.substring(0, 30) || (
                                    <span className="text-gray-400">
                                      Not available
                                    </span>
                                  )}
                                </p>
                              </TooltipTrigger>
                              <TooltipContent
                                side="bottom"
                                className="bg-black-900 text-white-A700 max-w-[200px] whitespace-normal break-words border-0"
                              >
                                <p className=" w-full font-normal text-base leading-[25px] ">
                                  {companyData?.contactAddress?.at(-1)}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <p className="border-dashed text-[#EBEBEB] border m-0.5 w-full"></p>
                {/* Right div */}
                <div className="bg-[#fafafa] p-2 flex sm:flex-row gap-3 w-full  rounded-xl relative">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger className="cursor-pointer" asChild>
                        {isEditing ? (
                          <div className="relative flex items-start">
                            <textarea
                              value={inputValue}
                              onChange={handleInputChange}
                              className="border rounded p-2 sm:w-full w-[400px] resize-none  focus:outline-none overflow-auto "
                              placeholder="Enter your notes here"
                              rows={4}
                              style={{ minHeight: "100px", maxHeight: "200px" }}
                            />
                            <button
                              onClick={handleSave}
                              className="mt-2 sm:mt-0 sm:ml-2 bg-blue-500 rounded px-4 py-2 sm:w-auto  ml-8 sm:ml-0"
                              style={{ color: "white" }}
                            >
                              Save
                            </button>
                          </div>
                        ) : (
                          <div className="whitespace-nowrap overflow-hidden text-ellipsis max-w-[400px] h-[100px]">
                            <p className="whitespace-normal break-words h-full overflow-hidden overflow-ellipsis">
                              {companyData?.notes &&
                              companyData.notes.length > 200
                                ? `${companyData.notes.substring(0, 200)}...`
                                : companyData?.notes || "No notes available"}
                            </p>
                          </div>
                        )}
                      </TooltipTrigger>
                      <TooltipContent
                        side="bottom"
                        className="bg-black-900 text-white-A700 max-w-xs whitespace-normal break-words border-0"
                      >
                        <p className="whitespace-normal max-w-xs break-words">
                          {companyData?.notes}
                        </p>
                      </TooltipContent>
                    </Tooltip>

                    {/* Right div - Edit button */}
                    {!checkLevel(LEVEL_2) && (
                      <div className="flex ml-auto cursor-pointer">
                        {!isEditing && (
                          <svg
                            onClick={handleEditClick}
                            width="20"
                            height="21"
                            viewBox="0 0 20 21"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="ml-2 cursor-pointer"
                          >
                            <g clipPath="url(#clip0_1689_36824)">
                              <path
                                d="M3.83594 4.82812H3.0026C2.56058 4.82813 2.13665 5.00372 1.82409 5.31628C1.51153 5.62884 1.33594 6.05276 1.33594 6.49479V13.9948C1.33594 14.4368 1.51153 14.8607 1.82409 15.1733C2.13665 15.4859 2.56058 15.6615 3.0026 15.6615H10.5026C10.9446 15.6615 11.3686 15.4859 11.6811 15.1733C11.9937 14.8607 12.1693 14.4368 12.1693 13.9948V13.1615"
                                stroke="#165FE3"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M11.3333 3.16676L13.8333 5.66676M14.9875 4.48759C15.3157 4.15938 15.5001 3.71424 15.5001 3.25009C15.5001 2.78594 15.3157 2.34079 14.9875 2.01259C14.6593 1.68438 14.2142 1.5 13.75 1.5C13.2858 1.5 12.8407 1.68438 12.5125 2.01259L5.5 9.00009V11.5001H8L14.9875 4.48759Z"
                                stroke="#165FE3"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_1689_36824">
                                <rect
                                  width="20"
                                  height="20"
                                  fill="white"
                                  transform="translate(0 0.5)"
                                />
                              </clipPath>
                            </defs>
                          </svg>
                        )}
                      </div>
                    )}
                  </TooltipProvider>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
          <div className="flex gap-5 w-full">
            {companyData?.customer ? (
              <DropdownMenu>
                <DropdownMenuTrigger className="text-[#23AB28] px-4 py-2 flex items-center justify-between bg-[#E1FFE2] rounded-md whitespace-nowrap font-inter text-sm font-medium leading-5 w-full">
                  <p className="w-full">Active</p>
                  {!checkLevel(LEVEL_2) && (
                    <svg
                      width="14"
                      height="8"
                      viewBox="0 0 14 8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7.0026 6.38987L1.18796 0.511079C1.1411 0.462677 1.08547 0.424413 1.02428 0.398498C0.963095 0.372583 0.89757 0.359533 0.831498 0.360103C0.765426 0.360673 0.700119 0.374853 0.639356 0.40182C0.578593 0.428787 0.52358 0.468007 0.477502 0.51721C0.431423 0.566414 0.395194 0.624623 0.37091 0.68847C0.346626 0.752317 0.33477 0.820536 0.336028 0.889175C0.337286 0.957815 0.351632 1.02551 0.378237 1.08835C0.404843 1.15119 0.443178 1.20793 0.491026 1.25527L6.65414 7.48575C6.74743 7.58005 6.87245 7.63281 7.0026 7.63281C7.13276 7.63281 7.25778 7.58005 7.35107 7.48575L13.5142 1.25527C13.562 1.20793 13.6004 1.15119 13.627 1.08835C13.6536 1.02551 13.6679 0.957814 13.6692 0.889174C13.6704 0.820535 13.6586 0.752317 13.6343 0.68847C13.61 0.624623 13.5738 0.566413 13.5277 0.51721C13.4816 0.468007 13.4266 0.428787 13.3659 0.40182C13.3051 0.374852 13.2398 0.360673 13.1737 0.360103C13.1076 0.359532 13.0421 0.372582 12.9809 0.398498C12.9197 0.424412 12.8641 0.462677 12.8173 0.511078L7.0026 6.38987Z"
                        fill="#23AB28"
                      />
                    </svg>
                  )}
                </DropdownMenuTrigger>
              </DropdownMenu>
            ) : (
              !checkLevel(LEVEL_2) && (
                <button
                  onClick={() => {
                    dispatch(setPhone(companyData?.contactNumber));
                    dispatch(setEmail(companyData?.contactEmail));
                    dispatch(setContactId(id as string));
                    navigate("/customerregistration", {
                      state: {
                        contact_name: companyData?.contactName,
                        contact_number: companyData?.contactNumber,
                        contact_email: companyData?.contactEmail,
                        currentpath: "contactDetails",
                        contactID: companyData?.id,
                        pathName: "ContactPage",
                      },
                    });
                  }}
                  className="flex w-full items-center gap-2 whitespace-nowrap rounded-md border border-blue-700 text-blue-700 px-4 py-2"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17.3833 7.76618C17.1697 7.06124 16.8578 6.39902 16.4635 5.79545C15.0876 3.68894 12.709 2.29688 10.0052 2.29688C5.74802 2.29688 2.29688 5.74802 2.29688 10.0052C2.29688 10.8803 2.44268 11.7212 2.71132 12.5052"
                      stroke="#165FE3"
                      stroke-linecap="round"
                    />
                    <path
                      d="M15.2135 10.0052C15.2135 8.95562 14.903 7.97852 14.369 7.16078C13.4393 5.73746 11.8321 4.79688 10.0052 4.79688C7.12872 4.79688 4.79688 7.12872 4.79688 10.0052C4.79688 11.2552 5.00521 13.5469 7.92187 17.7135"
                      stroke="#165FE3"
                      stroke-linecap="round"
                    />
                    <path
                      d="M17.7048 11.6719C17.0798 12.0885 15.9131 12.7552 14.5798 12.0885C12.7261 11.1617 13.1823 9.92954 12.2656 8.52612C11.7822 7.78597 10.9465 7.29688 9.99647 7.29688C8.12364 7.29688 7.46931 8.91813 7.28814 10.0052C7.10697 11.0923 7.49647 12.9219 8.74647 14.5885C9.64789 15.7904 11.2315 16.9493 12.2656 17.2964"
                      stroke="#165FE3"
                      stroke-linecap="round"
                    />
                    <path
                      d="M10 10.4219C10 13.3385 12.9167 15.0052 15.4167 15.4219"
                      stroke="#165FE3"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  Start KYC
                </button>
              )
            )}
            <DropdownMenu>
              <DropdownMenuTrigger className="text-[#333333] w-full px-4 py-2 rounded-md whitespace-nowrap font-inter text-sm font-medium leading-5 text-center border border-[#165FE3]">
                More Options
              </DropdownMenuTrigger>

              <DropdownMenuContent className="p-2">
                {!checkLevel(LEVEL_2) && (
                  <>
                    {companyData?.linked_partner_id === 0 && (
                      <LinkPartner
                        contact_name={companyData?.contactName}
                        showLinkPartnerButton={true}
                        otherProps={{ contact_id: id }}
                      >
                        <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                          <span className="text-[#333333] rounded-lg whitespace-nowrap font-inter text-sm font-medium leading-5">
                            Link Partner
                          </span>
                        </DropdownMenuItem>
                      </LinkPartner>
                    )}
                    {companyData?.linked_company_id === 0 && (
                      <LinkCorporate
                        contact_name={companyData?.contactName}
                        showLinkPartnerButton={true}
                        otherProps={{ contact_id: id }}
                      >
                        <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                          <span className="text-[#333333] rounded-lg whitespace-nowrap font-inter text-sm font-medium leading-5">
                            Link Corporate
                          </span>
                        </DropdownMenuItem>
                      </LinkCorporate>
                    )}
                  </>
                )}
                <div>
                  <DocumentModal fetchFn={getDocsForDownload} docs={docs}>
                    <button className="flex items-center gap-2.5 py-2 px-2.5">
                      <p className="text-blue-700 font-semibold text-sm">
                        {checkLevel(LEVEL_2) ? "View Documents" : "Documents"}
                      </p>
                    </button>
                  </DocumentModal>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div className="h-full bg-white-A700 p-2.5 relative flex flex-col rounded-lg overflow-auto gap-2">
          <div className="flex w-full justify-between items-center sm:px-0">
            <div className=" flex flex-col gap-2 w-full ">
              <Tabs
                className="flex gap-2 w-full"
                value={pipelineType}
                onValueChange={(value) => {
                  setActiveTab(value);
                }}
              >
                <TabsList className="h-[44px] p-0 w-full">
                  {tablist.map((tab) => (
                    <TabsTrigger
                      disabled={!tab.enabled}
                      key={tab.value}
                      value={tab.value}
                      className={` py-[18px] px-6 disabled:opacity-40 text-sm h-[44px] w-full font-semibold ${
                        tab.value === activeTab
                          ? "border-blue-700 bg-navbar-menu-bg border-t-2 text-blue-700"
                          : " text-black-label"
                      }`}
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>
            <div className="flex gap-3 sm:hidden">
              {!checkLevel(LEVEL_2) && (
                <button
                  className="disabled:opacity-40 border border-blue-700 text-[#165FE3] font-semibold px-4 py-3 rounded-lg"
                  onClick={() => {
                    dispatch(setIsReferralMade(true));
                    navigate("/lead/getquote", {
                      state: {
                        id: id,
                        contact_name: companyData?.contactName,
                        contact_number: companyData?.contactNumber,
                        contact_email: companyData?.contactEmail,
                        currentpath: "contactDetails",
                        contactID: companyData?.id,
                      },
                    });
                  }}
                >
                  Get Quote
                </button>
              )}
              {!checkLevel(LEVEL_2) && (
                <button
                  className="disabled:opacity-40 border border-blue-700 text-[#165FE3] font-semibold px-4 py-3 rounded-lg"
                  onClick={() => {
                    dispatch(setIsReferralMade(true));
                    navigate("/lead/retails/createlead", {
                      state: {
                        id: id,
                        contact_name: companyData?.contactName,
                        contact_number: companyData?.contactNumber,
                        contact_email: companyData?.contactEmail,
                        currentpath: "contactDetails",
                        contactID: companyData?.id,
                      },
                    });
                  }}
                >
                  New Referral Lead
                </button>
              )}
              <button
                onClick={() =>
                  navigate("/lead/retails/createlead", {
                    state: {
                      id,
                      contact_name: companyData?.contactName,
                      contact_number: companyData?.contactNumber,
                      contact_email: companyData?.contactEmail,
                      currentpath: "contactDetails",
                      contactID: companyData?.id,
                    },
                  })
                }
                className=" bg-blue-700 border border-blue-700 text-white-A700 px-4 py-3 rounded-lg"
              >
                {label}
              </button>
            </div>
          </div>
          {activeTab === "leads" && (
            <OpportunityCardList
              data={opportunityList}
              onRowClick={handleRowClick}
              isPaginationVisible={opportunityList.length > 10}
            />
          )}
          {activeTab === "orders" && (
            <DataTable
              columns={[
                {
                  header: "Order ID",
                },
                {
                  header: "Product",
                },
                {
                  header: "Assigned To",
                },
                {
                  header: "Created by",
                },
                {
                  header: "Lead Status",
                },
              ]}
              data={[]}
              onRowClick={handleRowClick}
              highlightRow={true}
              isToolbarVisible={false}
              isPaginationVisible={false}
            />
          )}
          {checkLevel(LEVEL_2) ? (
            <LeadOverView
              isOpen={isRowDetailsOpen && checkLevel(LEVEL_2)}
              onOpenChange={setIsRowDetailsOpen}
              rowData={selectedRowData}
              getOpportunityList={getOpportunityList}
            />
          ) : (
            <RowDetailsSheet
              isOpen={isRowDetailsOpen && !checkLevel(LEVEL_2)}
              onOpenChange={setIsRowDetailsOpen}
              rowData={selectedRowData}
              getOpportunityList={getOpportunityList}
            />
          )}
          <div className="text-center grid grid-cols-2 gap-3">
            <button
              onClick={() =>
                navigate(href, {
                  state: {
                    id,
                    contact_name: companyData?.contactName,
                    contact_number: companyData?.contactNumber,
                    contact_email: companyData?.contactEmail,
                  },
                })
              }
              type="button"
              className="px-4 py-[11px] col-span-2 w-full rounded-lg text-sm bg-blue-700 text-white-A700 border"
            >
              {label}
            </button>
            {!checkLevel(LEVEL_2) && (
              <button
                className="disabled:opacity-40 border whitespace-no-wrap text-sm border-blue-700 text-[#165FE3] font-semibold px-4 py-3 rounded-lg"
                onClick={() => {
                  dispatch(setIsReferralMade(true));
                  navigate("/lead/getquote", {
                    state: {
                      id: id,
                      contact_name: companyData?.contactName,
                      contact_number: companyData?.contactNumber,
                      contact_email: companyData?.contactEmail,
                      currentpath: "contactDetails",
                      contactID: companyData?.id,
                    },
                  });
                }}
              >
                Get Quote
              </button>
            )}
            {!checkLevel(LEVEL_2) && (
              <button
                className="disabled:opacity-40 border whitespace-nowrap text-sm border-blue-700 text-[#165FE3] font-semibold px-4 py-3 rounded-lg"
                onClick={() => {
                  dispatch(setIsReferralMade(true));
                  navigate("/lead/retails/createlead", {
                    state: {
                      id: id,
                      contact_name: companyData?.contactName,
                      contact_number: companyData?.contactNumber,
                      contact_email: companyData?.contactEmail,
                      currentpath: "contactDetails",
                      contactID: companyData?.id,
                    },
                  });
                }}
              >
                New Referral Lead
              </button>
            )}
          </div>
        </div>
      </div>
      <Toaster theme="dark" position="top-right" />
    </>
  );
}
// Card-based Opportunity List component
const OpportunityCardList = ({
  data,
  onRowClick,
  isPaginationVisible = true,
}: any) => {
  const [sorting, setSorting] = useState([{ id: "id", desc: true }]);

  // Create a single virtual column to render our cards
  const columnHelper = createColumnHelper();
  const columns = [
    columnHelper.accessor("id", {
      cell: ({ row }) => {
        const item = row.original as any;

        return (
          <div
            className="w-full h-full flex-1 border rounded-lg p-4 mb-3 hover:bg-gray-50 cursor-pointer"
            onClick={() => onRowClick(item)}
          >
            <div className="flex justify-between items-start mb-2">
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-lg">{item.product}</h3>
                  {item.requestQuote_Type && (
                    <div className="relative group flex items-center">
                      <div className="w-2.5 h-2.5 bg-red-500 rounded-full"></div>
                      <span className="absolute bottom-full left-0 mb-1 bg-gray-300 text-[#333] text-xs rounded-md py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                        Quotation request for ID: {item.id}
                      </span>
                    </div>
                  )}
                </div>
                <p className="text-sm text-gray-500">{item.subCategory}</p>
              </div>
              <div>
                {(() => {
                  if (!item.opportunityContactStatus) {
                    return (
                      <p className="py-1 px-3 rounded-full text-center bg-[#E1FFE2] text-[#23AB28]">
                        Open
                      </p>
                    );
                  }

                  const statusOption = close_reason_options.find(
                    (opt) => opt.value === item.opportunityContactStatus,
                  );

                  return (
                    <p
                      className={`py-1 px-3 rounded-full text-center ${statusOption?.bgColor} ${statusOption?.textColor}`}
                    >
                      {statusOption?.label}
                    </p>
                  );
                })()}
              </div>
            </div>

            <div className="flex items-center gap-1 text-gray-600 mb-2">
              <span className="font-medium">OP-{item.id}</span>
              <span className="mx-2">•</span>
              <span>{item.createDate}</span>
              <span className="ml-1 text-xs text-gray-500">
                ({item.createdBy})
              </span>
            </div>

            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2 mb-2">
                <div className="flex items-center">
                  <svg
                    viewBox="0 0 24 24"
                    className="w-5 h-5 text-gray-500"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z"
                      fill="currentColor"
                    />
                  </svg>
                  <span className="ml-1">{item.assignedTo}</span>
                </div>
                <span className="text-gray-400 mx-1">•</span>
                <div className="text-sm text-gray-500">{item.location}</div>
              </div>
              {/* <div className="flex items-center">
                  <span className="text-gray-600">Assigned to:</span>
                  <span className="ml-1 font-medium">{item.assignedTo}</span>
                </div> */}

              <div className="flex items-center">
                {(() => {
                  const type = (
                    item?.lead_deatils?.Type as string | undefined
                  )?.toLowerCase();
                  const newSource =
                    type &&
                    (channel_options as Record<string, any>)?.[type]?.find(
                      (opt) =>
                        opt.value?.toLowerCase() ===
                        (item.source as string)
                          ?.toLowerCase()
                          .split(" ")
                          .join(""),
                    );

                  return (
                    <div className="flex items-center gap-2">
                      <span className="text-sm">
                        {item?.lead_deatils?.id === 12
                          ? "Partner referral"
                          : item?.lead_deatils?.id === 18
                            ? "Employee referral"
                            : newSource?.label || item.source}
                      </span>
                      {newSource?.icon && (
                        <Img
                          className="h-5 w-5"
                          src={newSource.icon}
                          alt="icon"
                        />
                      )}
                    </div>
                  );
                })()}

                {checkLevel(LEVEL_2) && item.latestQuotationPDF && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDownloadViaURL(item.latestQuotationPDF);
                    }}
                    className="ml-3"
                  >
                    <svg
                      width="25"
                      height="24"
                      viewBox="0 0 25 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3.59375 5.43359C3.59375 2.67217 5.83232 0.433594 8.59375 0.433594H15.537C15.8914 0.433594 16.2291 0.583994 16.4661 0.847394L21.3979 6.32706C21.6045 6.55659 21.7188 6.85446 21.7188 7.16327V18.5586C21.7188 21.32 19.4801 23.5586 16.7188 23.5586H8.59375C5.83232 23.5586 3.59375 21.32 3.59375 18.5586V5.43359Z"
                        fill="#165FE3"
                      />
                      <path
                        d="M21.7188 7.93359V7.16327C21.7188 6.85446 21.6045 6.55659 21.3979 6.32706L16.4661 0.847394C16.2291 0.583994 15.8914 0.433594 15.537 0.433594H14.2188V2.93359C14.2188 5.69502 16.4574 7.93359 19.2188 7.93359H21.7188Z"
                        fill="#98BDFF"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M8.26385 14.4462C8.64095 14.0913 9.23427 14.1094 9.58906 14.4865L12.6562 17.7467L15.7235 14.4865C16.0782 14.1094 16.6715 14.0913 17.0486 14.4462C17.4257 14.8009 17.4439 15.3942 17.089 15.7713L13.7179 19.3545C13.1385 19.9704 12.174 19.9704 11.5946 19.3545L8.22345 15.7713C7.86866 15.3942 7.88675 14.8009 8.26385 14.4462Z"
                        fill="white"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M12.6562 8.56641C13.174 8.56641 13.5938 8.98614 13.5938 9.50391V18.8789C13.5938 19.3967 13.174 19.8164 12.6562 19.8164C12.1385 19.8164 11.7188 19.3967 11.7188 18.8789V9.50391C11.7188 8.98614 12.1385 8.56641 12.6562 8.56641Z"
                        fill="white"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      },
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  return (
    <div className="space-y-4 h-full flex-1 overflow-auto">
      <div className="rounded-md">
        {table.getRowModel().rows.map((row) => (
          <div key={row.id}>
            {flexRender(
              row.getVisibleCells()[0].column.columnDef.cell,
              row.getVisibleCells()[0].getContext(),
            )}
          </div>
        ))}
      </div>

      {isPaginationVisible && data.length > 10 && (
        <div className="flex items-center justify-end space-x-2 py-4">
          <button
            className="rounded border p-1"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            {"<"}
          </button>
          <span className="flex items-center gap-1">
            <div>Page</div>
            <strong>
              {table.getState().pagination.pageIndex + 1} of{" "}
              {table.getPageCount()}
            </strong>
          </span>
          <button
            className="rounded border p-1"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            {">"}
          </button>
        </div>
      )}
    </div>
  );
};

// Replace your current DataTable with this component
// <DataTable columns={filteredColumns} data={opportunityList} ... />

// Instead use:
